import React, { memo, type ReactElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodMitigateModal,
  SodRemediateModal,
  SodReviewModal,
} from '../../../shared/components';
import {
  CF_INF_MED,
  CF_NEU_BLD,
  CF_NEU_MED,
  RENDER_MODES,
  VIOLATIONS_INI_CALL,
  violationsHome,
} from '../../../shared/constants';
import {
  useInvertedLocalize,
  // usePersonalization, IS_READ_ONLY
  useTypedSelector,
} from '../../../shared/hooks';
import {
  globalActions,
  violationsTabActions,
} from '../../../shared/store/actions';
import { testIds } from '../../../tests';
import {
  useColumnPicker,
  useFiltersInitialization,
  useGetQueryMap,
  useGetSortMenu,
  usePageTracker,
  useTabFilters,
} from '../hooks';

import ReadOnlyGraph from './ViolationsTabGraph/ReadOnlyGraph';
import ViolationsControls from './ViolationsControls';
import ViolationsInsights from './ViolationsInsights';
import ViolationsSidePanel from './ViolationsSidePanel';
import ViolationsTabFooter from './ViolationsTabFooter';
// import ViolationsTabGraph from './ViolationsTabGraph';
import ViolationsTabList from './ViolationsTabList';
import ViolationsWidgets from './ViolationsWidgets';

import './ViolationsTab.css';

/**
 * ViolationsTab
 *
 * A component that manages and displays violations data in both list and graph modes.
 * It handles the initialization of filters, manages different view modes, and coordinates the display
 * of violations data along with related widgets and side panels.
 *
 * @returns {ReactElement} Returns a section containing violations data visualization
 *
 * @example
 * <ViolationsTab />
 *
 * @dependencies
 * - react
 * - react-redux
 * - @saviynt/design-system
 *
 * @features
 * - Supports both list and graph view modes
 * - Integrates with SodInsights component
 * - Handles advanced and tab filters initialization
 * - Manages side panel visibility
 * - Supports bulk actions with footer
 * - Includes violations widgets in graph mode
 *
 * @state
 * The component relies on the following Redux state:
 * - activeViolationsTab: Current active violations tab
 * - renderMode: Current view mode (list/graph)
 * - isInsightsOpen: Visibility state of insights panel
 * - isSidePanelVisible: Visibility state of side panel
 * - isFooterVisible: Visibility state of footer
 *
 * @hooks
 * - useDispatch: For dispatching Redux actions
 * - useInvertedLocalize: For localization
 * - useTypedSelector: For accessing Redux state
 * - useFiltersInitialization: For initializing advanced filters
 * - useTabFilters: For managing tab filters
 * - usePageTracker: For tracking page state
 * - useGetQueryMap: For handling query parameters
 */
function ViolationsTab(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // To save personalization
  // IS_READ_ONLY
  // usePersonalization();

  // Initial query hooks
  const queryMap = useGetQueryMap(true);
  const { updateTracker } = usePageTracker(Object.values(VIOLATIONS_INI_CALL));
  const { initializeAdvancedFilters } = useFiltersInitialization(queryMap, l);
  const { initializeTabFilters } = useTabFilters();
  const { initializeViewColumns } = useColumnPicker();

  const {
    user: { isMenuPinned },
    aag: {
      global: { activeViolationsTab, isGlobalLoading },
      violationsTab: {
        controls: { renderMode, isInsightsOpen, sortInAction },
        bulkaction: { isFooterVisible },
        sidepanel: { isSidePanelVisible },
        modalIsland: {
          isMitigateModalOpen,
          isRemediateModalOpen,
          isReviewModalOpen,
        },
      },
    },
  } = useTypedSelector((state) => state);

  // Enable sort hook
  useGetSortMenu(sortInAction);

  // On page load
  useEffect(() => {
    if (activeViolationsTab === violationsHome) {
      const initializeFilters = async () => {
        // Order is important
        const response = await Promise.all([
          initializeAdvancedFilters(),
          initializeTabFilters(),
          initializeViewColumns(),
        ]);

        // Raise global error is any one fails
        const isFalseValue = response.some((value) => !value);

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Don't load page if untill all pass
        Object.values(VIOLATIONS_INI_CALL).forEach((callValue, index) => {
          updateTracker(callValue, response[index]);
        });
      };

      initializeFilters().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViolationsTab]);

  const isGraphMode = Boolean(renderMode === RENDER_MODES.GRAPH);
  const isListMode = Boolean(renderMode === RENDER_MODES.LIST);
  const renderContent = useMemo(() => {
    if (isGlobalLoading) {
      return null;
    }

    if (isGraphMode) {
      // return <ViolationsTabGraph />;
      // IS_READ_ONLY
      return (
        <Box tag='div' className='SodReadOnlyGraph-wrapper'>
          <div className='SodReadOnlyGraph-wrapperBg' />
          <Box tag='div' className='SodReadOnlyGraph-topNote'>
            <Typography kind='body1' color={CF_INF_MED}>
              {l('COMING SOON')}
            </Typography>
            <Typography kind='h1' color={CF_NEU_BLD}>
              {l('Interactive Violation Graph')}
            </Typography>
            <Typography kind='body2' color={CF_NEU_MED}>
              {l(
                'This upcoming graph will visualize the same violation data shown in the list view, displayed across a risk score and age matrix. It offers a more interactive way to explore violations, with built-in insights and filters to help you identify trends and outliers.'
              )}
            </Typography>
            <Button
              type='button'
              kind='filled'
              size='medium'
              dataTheme='dark'
              onClick={() => {
                dispatch(violationsTabActions.setRenderMode(RENDER_MODES.LIST));
              }}
              leftIcon={<Icon kind='List' />}
              className='SodReadOnlyGraph-RedirectButton'>
              {l('Back to List View')}
            </Button>
          </Box>
          <ReadOnlyGraph className='SodReadOnlyGraph-graph' />
        </Box>
      );
    }

    return (
      <>
        <ViolationsTabList />
        <ViolationsTabFooter />
      </>
    );
  }, [l, dispatch, isGraphMode, isGlobalLoading]);

  const violationsTabClasses = useMemo(
    () => cx('Sod-ViolationsTab', isListMode && 'Sod-ViolationsTab--isList'),
    [isListMode]
  );
  const violationsTabViewClasses = useMemo(
    () =>
      cx(
        'Sod-ViolationsTab-view',
        isGraphMode && 'Sod-ViolationsTab-view--isGraph',
        isListMode && 'Sod-ViolationsTab-view--isList',
        isSidePanelVisible && 'Sod-ViolationsTab-view--isSidePanelVisible',
        isInsightsOpen && 'Sod-ViolationsTab-view--isInsightsOpen',
        isFooterVisible && 'Sod-ViolationsTab-view--isFooterVisible',
        !isSidePanelVisible &&
          !isInsightsOpen &&
          'Sod-ViolationsTab-view--isNotAsides',
        isMenuPinned && 'Sod-ViolationsTab-view--isMenuPinned'
      ),
    [
      isGraphMode,
      isListMode,
      isSidePanelVisible,
      isInsightsOpen,
      isFooterVisible,
      isMenuPinned,
    ]
  );

  return (
    <Box
      tag='section'
      className={violationsTabClasses}
      dataTestId={testIds.SOD_VIOLATIONS_PAGE_BODY}>
      <Box
        className={violationsTabViewClasses}
        dataTheme={isGraphMode ? 'dark' : undefined}>
        <Box className='Sod-ViolationsView-wrapper'>
          <Box className='Sod-ViolationsView-content'>
            {/* IS_READ_ONLY */}
            {isListMode && <ViolationsControls isListMode={isListMode} />}
            {renderContent}
          </Box>
          <Box className='Sod-ViolationsView-asides'>
            <ViolationsInsights className='Sod-ViolationsView-asidesInsight' />
            <ViolationsSidePanel />
          </Box>
        </Box>
      </Box>
      {isGraphMode && <ViolationsWidgets />}
      {/* Modals */}
      <SodMitigateModal
        isOpen={isMitigateModalOpen}
        setIsOpen={() => {
          dispatch(violationsTabActions.setIsMitigateModalOpen(false));
        }}
      />
      <SodRemediateModal
        isOpen={isRemediateModalOpen}
        setIsOpen={() => {
          dispatch(violationsTabActions.setIsRemediateModalOpen(false));
        }}
      />
      <SodReviewModal
        isOpen={isReviewModalOpen}
        setIsOpen={() => {
          dispatch(violationsTabActions.setIsReviewModalOpen(false));
        }}
      />
    </Box>
  );
}

export default memo(ViolationsTab);
